import React, { useEffect, useState } from 'react'
import { Alert, Card, Form, Spinner, Button } from 'react-bootstrap'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'

interface PermissionsSelectorProps {
  selectedPermissions: string[]
  onChange: (permissions: string[]) => void
}

interface PermissionGroup {
  category: string
  permissions: {
    key: string
    displayName: string
  }[]
}

export const PermissionsSelector: React.FC<PermissionsSelectorProps> = ({
  selectedPermissions,
  onChange
}) => {
  const httpClient = useAxiosHttpClient()
  const [availablePermissions, setAvailablePermissions] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAvailablePermissions()
  }, [])

  const loadAvailablePermissions = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await httpClient.get?.({
        url: `${URL_API}/permissions/available`
      })

      setAvailablePermissions(response?.grouped || {})
    } catch (err: any) {
      console.error('Error loading available permissions:', err)
      setError('Não foi possível carregar as permissões disponíveis')
    } finally {
      setLoading(false)
    }
  }

  const getCategoryDisplayName = (category: string): string => {
    const names: { [key: string]: string } = {
      spes: 'SPEs',
      projects: 'Empreendimentos',
      units: 'Unidades',
      contracts: 'Contratos',
      billings: 'Pagamentos',
      tokens: 'Tokens',
      documents: 'Documentos',
      reports: 'Relatórios',
      users: 'Usuários',
      roles: 'Funções',
      permissions: 'Permissões',
      companies: 'Empresas',
      receivables: 'Recebíveis',
      standalone_billings: 'Cobranças Avulsas',
      bank_statement_entries: 'Conciliação do Extrato'
    }
    return names[category] || category
  }

  const getActionDisplayName = (action: string): string => {
    const actions: { [key: string]: string } = {
      read: 'Visualizar',
      create: 'Criar',
      update: 'Editar',
      delete: 'Excluir',
      manage: 'Gerenciar',
      sign: 'Assinar',
      register_blockchain: 'Registrar na Blockchain',
      process_payment: 'Processar Pagamento',
      export: 'Exportar',
      export_report: 'Exportar Relatório',
      view_dashboard: 'Ver Dashboard',
      mint: 'Cunhar',
      transfer: 'Transferir',
      upload: 'Fazer Upload',
      view_financial: 'Ver Financeiro',
      view_contracts: 'Ver Contratos',
      invite: 'Convidar',
      remove: 'Remover'
    }
    return actions[action] || action
  }

  const handleToggle = (permissionKey: string) => {
    const newPermissions = selectedPermissions.includes(permissionKey)
      ? selectedPermissions.filter((p) => p !== permissionKey)
      : [...selectedPermissions, permissionKey]

    onChange(newPermissions)
  }

  const handleSelectAllInCategory = (category: string) => {
    const categoryPermissions = Object.values(
      availablePermissions[category]
    ).map((p: any) => `${p.resource}.${p.action}`)

    const allSelected = categoryPermissions.every((p) =>
      selectedPermissions.includes(p)
    )

    if (allSelected) {
      // Deselect all in category
      onChange(
        selectedPermissions.filter((p) => !categoryPermissions.includes(p))
      )
    } else {
      // Select all in category
      const newPermissions = [
        ...selectedPermissions,
        ...categoryPermissions.filter((p) => !selectedPermissions.includes(p))
      ]
      onChange(newPermissions)
    }
  }

  const handleSelectAll = () => {
    if (selectedPermissions.length === getTotalPermissionsCount()) {
      onChange([])
    } else {
      const allPermissions = Object.values(availablePermissions).flatMap(
        (category: any) =>
          Object.values(category).map((p: any) => `${p.resource}.${p.action}`)
      )
      onChange(allPermissions)
    }
  }

  const getTotalPermissionsCount = () => {
    if (!availablePermissions) return 0
    return Object.values(availablePermissions).reduce(
      (total: number, category: any) => total + Object.keys(category).length,
      0
    )
  }

  if (loading) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="mr-2" />
          <span>Carregando permissões...</span>
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    )
  }

  if (!availablePermissions || Object.keys(availablePermissions).length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        <i className="feather icon-info mr-2" />
        Nenhuma permissão disponível
      </Alert>
    )
  }

  return (
    <Card className="mt-3">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <strong>
            <i className="feather icon-shield mr-2" />
            Selecione as Permissões
          </strong>
          <Button
            variant="link"
            size="sm"
            onClick={handleSelectAll}
            className="p-0"
          >
            {selectedPermissions.length === getTotalPermissionsCount()
              ? 'Desmarcar Todas'
              : 'Selecionar Todas'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
        {Object.entries(availablePermissions).map(
          ([category, permissions]: [string, any]) => {
            const categoryPermissions = Object.values(permissions).map(
              (p: any) => `${p.resource}.${p.action}`
            )
            const allCategorySelected = categoryPermissions.every((p) =>
              selectedPermissions.includes(p)
            )

            return (
              <div key={category} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    <i className="feather icon-layers mr-1" />
                    {getCategoryDisplayName(category)}
                  </h6>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handleSelectAllInCategory(category)}
                    className="p-0"
                    style={{ fontSize: '0.85rem' }}
                  >
                    {allCategorySelected ? 'Desmarcar' : 'Selecionar'} Todas
                  </Button>
                </div>
                <div className="pl-3">
                  {Object.values(permissions).map((permission: any) => {
                    const permissionKey = `${permission.resource}.${permission.action}`
                    // Special case for users.create - it's an invitation, not creation
                    const displayLabel =
                      permissionKey === 'users.create'
                        ? 'Convidar'
                        : getActionDisplayName(permission.action)

                    return (
                      <Form.Check
                        key={permissionKey}
                        type="checkbox"
                        id={`perm-${permissionKey}`}
                        label={displayLabel}
                        checked={selectedPermissions.includes(permissionKey)}
                        onChange={() => handleToggle(permissionKey)}
                        className="mb-2"
                      />
                    )
                  })}
                </div>
              </div>
            )
          }
        )}
      </Card.Body>
      <Card.Footer>
        <small className="text-muted">
          <i className="feather icon-check mr-1" />
          {selectedPermissions.length} de {getTotalPermissionsCount()}{' '}
          permissão(ões) selecionada(s)
        </small>
      </Card.Footer>
    </Card>
  )
}
